import { useState } from "react";
import { format } from "date-fns";

import DatePicker from "componentsv2/form/DatePicker";
import DatetimePicker from "componentsv2/form/DatetimePicker";
import Dropdown from "componentsv2/form/Dropdown";
import Selection from "componentsv2/form/Selection";
import Text from "componentsv2/form/Text";
import Button from "componentsv2/button/Button";
import BasicTable from "componentsv2/table/BasicTable";
import { layoutShift, IS_BILLABLE } from "./layoutShift";
import { useWizard } from "storev2/wizardStore";
import { useTable } from "storev2/tableStore";
import { useLayout } from "hooks/layout";
import proxy from "api/proxy";
import { getSections } from "./services";
import { getStorage } from "store/local";
import { isChangeShiftWithinLimit } from "./helper";

const model = "staff.shift";

function WizardCreateShifts({ onClose, args }) {
  const [msg, setMsg] = useState(null);
  const [records, setRecords] = useState(null);
  const { store } = useWizard();
  const layout = useLayout(layoutShift);
  const { setTable, getTable, setDomain } = useTable();
  const storeId = "staff.shift";
  const {
    selectedDate,
    section,
    activity,
    position,
    notes,
    billable,
    enterTimestamp,
    exitTimestamp,
  } = args.initialState;
  const extraModules = args.extraModules;
  const crmStaff = extraModules?.crm_staff === "activated";
  const accessBill = extraModules?.staff_access_bill === "activated";

  const fieldStart = {
    label: "staff.shift_scheduling.start_date",
    name: "start_date",
    onChange: onChange,
    required: true,
  };

  const attrsSection = {
    label: "staff.shift_scheduling.section",
    name: "section",
    type: "many2one",
    model: "staff.section",
    onChange: onChange,
    searchable: true,
    preloaded: true, // This if for many2one with less 200 registers
    required: true,
    preOptions: getSections,
  };

  const attrsActivity = {
    label: "staff.shift.activity",
    name: "activity",
    type: "many2one",
    model: "staff.event_category",
    onChange: onChange,
    required: true,
    preOptions: () => Array.from(args.activities.values()),
    preloaded: true,
  };

  const attrsPosition = {
    label: "staff.position",
    name: "position",
    type: "many2one",
    model: "staff.position",
    onChange: onChange,
    required: true,
    // preOptions: () => Array.from(args.activities.values()),
    // preloaded: true,
  };

  const attrsBillable = {
    label: "staff.shift.is_billable",
    name: "is_billable",
    type: "selection",
    onChange: onChange,
    options: IS_BILLABLE,
    required: true,
  };

  const fieldNotes = {
    label: "staff.shift_scheduling.notes",
    name: "notes",
    onChange: onChange,
    required: true,
    colSpan: "col-span-2",
  };

  async function onChange(name, value) {
    store[name] = value;
    let startSearch;
    if (
      accessBill &&
      store.activity &&
      store.start_date &&
      store.section &&
      store.is_billable &&
      store.notes
    ) {
      startSearch = true;
    }
    if (crmStaff && store.start_date && store.section && store.position) {
      startSearch = true;
    }
    if (startSearch) {
      const staffConfig = getStorage("staffConfig");
      if (!isChangeShiftWithinLimit(store.start_date, staffConfig?.change_shift_limit)) {
        setMsg("No está permitido modificar turnos para la fecha seleccionada según el límite configurado.");
        setRecords([]);
        return;
      }
      const domain = [
        ["shift_date", "=", store.start_date],
        ["section", "=", store.section.id],
        ["state", "!=", "cancelled"],
      ];
      await setDomain("search", layout, storeId, domain);
      const args = {
        layout,
        key: storeId,
      };
      const table_ = await setTable(args);
      setRecords(table_.records);
      setMsg(null);
    }
  }

  async function onClickUpdate() {
    const fields_names = ["id"];
    const _table = getTable(model);
    if (_table) {
      for (const rec of _table.selected.values()) {
        const storeRec = {
          id: rec.id,
          notes: store.notes,
        };
        if (accessBill) {
          storeRec.activity = store.activity.id;
          storeRec.is_billable = store.is_billable;
        }
        if (crmStaff && store.position) {
          const model = "staff.shift";
          const method = "get_opp_line";
          const { data } = await proxy.methodInstance({
            model,
            method,
            instance: rec.id,
          });
          storeRec.position = store.position.id;
          storeRec.cost_uom = data.cost_uom;
          storeRec.shift_amount = data.cost_price;
          storeRec.unit_price = data.unit_price;
        }
        if (store.enter_timestamp) {
          storeRec.enter_timestamp = store.enter_timestamp;
        }
        if (store.exit_timestamp) {
          storeRec.exit_timestamp = store.exit_timestamp;
        }
        const values = { model, storeRec, fields_names };
        await proxy.saveQuery(values);
      }
    }
    // We need reload the table of shift scheduling
    onClose();
    await args.refetch();
  }

  const attrsShift = {
    name: "shifts",
    layout: layout,
    selectable: "multi",
  };

  const fieldEnterTime = {
    label: "staff.shift.enter_timestamp",
    name: "enter_timestamp",
    type: "datetime",
    onChange: onChange,
  };

  const fieldExitTime = {
    label: "staff.shift.exit_timestamp",
    name: "exit_timestamp",
    type: "datetime",
    onChange: onChange,
  };

  return (
    <div className="h-4/5 justify-between">
      <div className="grid grid-cols-4 gap-x-4 h-auto my-4">
        <DatePicker {...fieldStart} data={selectedDate} />
        <Dropdown {...attrsSection} data={section} />
        {accessBill && (
          <div>
            <Dropdown {...attrsActivity} data={activity} />
            <Selection {...attrsBillable} data={billable} />
          </div>
        )}
        {crmStaff && <Dropdown {...attrsPosition} data={position} />}
        <Text {...fieldNotes} data={notes} />
        <DatetimePicker {...fieldEnterTime} data={enterTimestamp} />
        <DatetimePicker {...fieldExitTime} data={exitTimestamp} />
      </div>
      {msg && (
        <div>
          <span>{msg}</span>
        </div>
      )}
      {records?.length > 0 && (
        <BasicTable storeId={storeId} {...attrsShift} records={records} />
      )}
      <div className="flex flex-col mt-4 justify-end h-full">
        <Button
          onClick={onClickUpdate}
          name="update"
          color="blue"
          content="staff.shift_scheduling.update"
          style="ml-auto mr-6"
        />
      </div>
    </div>
  );
}

export default WizardCreateShifts;
